'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Plays one track at a time for "listen before you pick" — starting another
 * track stops the first, and everything stops when the component unmounts.
 */
export function useAudioPreview() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playRequestRef = useRef(0);
  const [playingId, setPlayingId] = useState<string | null>(null);

  const stop = useCallback(() => {
    playRequestRef.current += 1;
    audioRef.current?.pause();
    setPlayingId(null);
  }, []);

  const toggle = useCallback(
    (id: string, url: string) => {
      if (playingId === id) {
        stop();
        return;
      }
      if (!audioRef.current) {
        audioRef.current = new Audio();
        audioRef.current.addEventListener('ended', () => setPlayingId(null));
      }
      const audio = audioRef.current;
      const playRequest = ++playRequestRef.current;
      audio.pause();
      audio.src = url;
      audio.currentTime = 0;
      setPlayingId(id);
      audio.play().catch(() => {
        if (playRequestRef.current === playRequest) setPlayingId(null);
      });
    },
    [playingId, stop]
  );

  useEffect(
    () => () => {
      playRequestRef.current += 1;
      audioRef.current?.pause();
    },
    []
  );

  return { playingId, toggle, stop };
}
